import { useState, useMemo, useEffect } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { validatePaymentVoucher } from "@/lib/accounting/engine";
import { VoucherLine, InvoiceAllocation, Voucher } from "@/lib/accounting/types";
import { PartyAutocomplete } from "@/components/accounting/PartyAutocomplete";
import { Button } from "@/components/ui/button";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CreditCard, Landmark, Wallet, CheckCircle2, IndianRupee, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface PaymentVoucherFormProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  preselectedPartyId?: string;
  preselectedInvoice?: Voucher;
}

export function PaymentVoucherForm({
  trigger,
  onSuccess,
  isOpen,
  onOpenChange,
  preselectedPartyId,
  preselectedInvoice,
}: PaymentVoucherFormProps) {
  const { ledgers, vouchers, createVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [recipientType, setRecipientType] = useState<"party" | "expense">("party");
  const [partyLedgerId, setPartyLedgerId] = useState(preselectedPartyId || "");
  const [expenseLedgerId, setExpenseLedgerId] = useState("");
  const [bankLedgerId, setBankLedgerId] = useState("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [amount, setAmount] = useState<string>(preselectedInvoice ? String(preselectedInvoice.totalDebit) : "");
  const [instrumentNo, setInstrumentNo] = useState("");
  const [instrumentDate, setInstrumentDate] = useState("");
  const [bankName, setBankName] = useState("");
  const [narration, setNarration] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);

  // Filter Cash and Bank Ledgers for paying source
  const cashBankLedgers = useMemo(
    () => ledgers.filter((l) => l.group === "Cash-in-Hand" || l.group === "Bank Accounts"),
    [ledgers]
  );

  // Filter Expense / Direct / Indirect Expense ledgers
  const expenseLedgers = useMemo(
    () =>
      ledgers.filter((l) =>
        ["Direct Expenses", "Indirect Expenses", "Purchase Accounts", "Current Liabilities"].includes(l.group)
      ),
    [ledgers]
  );

  // Default bank ledger
  useEffect(() => {
    if (!bankLedgerId && cashBankLedgers.length > 0) {
      const defaultBank = cashBankLedgers.find((l) => l.group === "Bank Accounts") || cashBankLedgers[0];
      setBankLedgerId(defaultBank.id);
    }
  }, [cashBankLedgers, bankLedgerId]);

  // Find open purchase invoices for this party to allocate payment against
  const openInvoices = useMemo(() => {
    if (!partyLedgerId) return [];
    return vouchers.filter(
      (v) =>
        v.type === "Purchase" &&
        v.status === "Approved" &&
        (v.partyLedgerId === partyLedgerId || v.partyName?.toLowerCase() === ledgers.find((l) => l.id === partyLedgerId)?.name?.toLowerCase()) &&
        (v.paymentStatus !== "Paid" || (v.paidAmount || 0) < v.totalDebit)
    );
  }, [vouchers, partyLedgerId, ledgers]);

  // Pre-select invoice if passed in props
  useEffect(() => {
    if (preselectedInvoice && preselectedInvoice.partyLedgerId) {
      setPartyLedgerId(preselectedInvoice.partyLedgerId);
      const remaining = preselectedInvoice.totalDebit - (preselectedInvoice.paidAmount || 0);
      setAmount(String(remaining > 0 ? remaining : preselectedInvoice.totalDebit));
      setSelectedInvoices({ [preselectedInvoice.id]: remaining > 0 ? remaining : preselectedInvoice.totalDebit });
    }
  }, [preselectedInvoice]);

  const numAmount = parseFloat(amount) || 0;
  const payingLedger = useMemo(() => cashBankLedgers.find((l) => l.id === bankLedgerId), [cashBankLedgers, bankLedgerId]);
  const partyLedger = useMemo(() => ledgers.find((l) => l.id === partyLedgerId), [ledgers, partyLedgerId]);
  const expenseLedger = useMemo(() => ledgers.find((l) => l.id === expenseLedgerId), [ledgers, expenseLedgerId]);

  const debitLedger = recipientType === "party" ? partyLedger : expenseLedger;

  const handleAllocateInvoice = (invId: string, invAmount: number, checked: boolean) => {
    setSelectedInvoices((prev) => {
      const updated = { ...prev };
      if (checked) {
        updated[invId] = invAmount;
      } else {
        delete updated[invId];
      }
      // Auto-sum selected invoices into total amount
      const sum = Object.values(updated).reduce((s, a) => s + a, 0);
      if (sum > 0) setAmount(String(sum));
      return updated;
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!payingLedger) {
      toast({ title: "Account Required", description: "Please select the Cash or Bank account you are paying from.", variant: "destructive" });
      return;
    }
    if (!debitLedger) {
      toast({ title: "Recipient Required", description: "Please select the party or expense account being paid.", variant: "destructive" });
      return;
    }
    if (numAmount <= 0) {
      toast({ title: "Invalid Amount", description: "Payment amount must be greater than zero.", variant: "destructive" });
      return;
    }

    const validation = validatePaymentVoucher(payingLedger.group, numAmount);
    if (!validation.valid) {
      toast({ title: "Payment Rule Violation", description: validation.error, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // In double-entry accounting:
      // Debit: Supplier / Creditor / Expense (Money given / liability reduced)
      // Credit: Cash or Bank account (Money leaving the business)
      const lines: VoucherLine[] = [
        {
          id: crypto.randomUUID(),
          ledgerId: debitLedger.id,
          ledgerName: debitLedger.name,
          debit: numAmount,
          credit: 0,
          narration: `Payment to ${debitLedger.name} via ${paymentMode}`,
        },
        {
          id: crypto.randomUUID(),
          ledgerId: payingLedger.id,
          ledgerName: payingLedger.name,
          debit: 0,
          credit: numAmount,
          narration: `Disbursement from ${payingLedger.name}`,
        },
      ];

      // Build allocations list
      const allocations: InvoiceAllocation[] = Object.entries(selectedInvoices).map(([invId, allocAmt]) => {
        const inv = vouchers.find((v) => v.id === invId);
        return {
          invoiceId: invId,
          invoiceNumber: inv?.invoiceNumber || inv?.voucherNumber || invId,
          allocatedAmount: allocAmt,
        };
      });

      const defaultNarration = `Payment of ₹${numAmount.toLocaleString("en-IN")} to ${debitLedger.name} via ${paymentMode}${
        instrumentNo ? ` | Ref: ${instrumentNo}` : ""
      }`;

      const finalNarration = narration.trim() || defaultNarration;

      const result = await createVoucher(
        "Payment",
        lines,
        finalNarration,
        recipientType === "party" ? debitLedger.id : undefined,
        undefined,
        0, 0, 0, 0,
        undefined,
        {
          date,
          paymentMode,
          instrumentNo: instrumentNo.trim() || undefined,
          instrumentDate: instrumentDate || undefined,
          bankName: bankName.trim() || payingLedger.name,
          bankLedgerId: payingLedger.id,
          allocations: allocations.length > 0 ? allocations : undefined,
        }
      );

      if (result.success && result.voucher) {
        toast({
          title: "Payment Recorded ✓",
          description: `${result.voucher.voucherNumber}: ₹${numAmount.toLocaleString("en-IN")} paid to ${debitLedger.name}.`,
        });
        setOpen(false);
        setAmount("");
        setInstrumentNo("");
        setNarration("");
        setSelectedInvoices({});
        onSuccess?.();
      } else {
        toast({ title: "Payment Failed", description: result.error || "Unable to save payment.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      size="transaction"
      trigger={
        trigger ? (
          trigger
        ) : isOpen === undefined ? (
          <Button variant="outline" className="gap-2">
            <CreditCard className="h-4 w-4" />
            <span>Pay Money (F5)</span>
          </Button>
        ) : undefined
      }
      icon={<CreditCard className="h-5 w-5 text-red-500" />}
      title="Record Payment Voucher (F5)"
      description="Record money leaving your business for supplier settlements, expenses, refunds, or liabilities."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          disabled={submitting}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="payment-voucher-form"
          disabled={submitting}
          className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          {submitting ? "Saving Payment..." : "Record Payment Voucher"}
        </Button>
      }
    >
      <form id="payment-voucher-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient Type */}
          <div className="flex items-center gap-4 border-b border-border pb-2 text-xs">
            <label className="font-semibold text-muted-foreground uppercase tracking-wider">Payee Type:</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="recipientType"
                  checked={recipientType === "party"}
                  onChange={() => setRecipientType("party")}
                  className="text-primary"
                />
                <span>Party (Supplier / Vendor)</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="recipientType"
                  checked={recipientType === "expense"}
                  onChange={() => setRecipientType("expense")}
                  className="text-primary"
                />
                <span>Expense / Direct Ledger</span>
              </label>
            </div>
          </div>

          {/* Payee Selection & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                {recipientType === "party" ? "Paid To (Supplier / Party) *" : "Expense / Account *"}
              </label>
              {recipientType === "party" ? (
                <PartyAutocomplete
                  value={partyLedgerId}
                  onSelect={(id) => setPartyLedgerId(id)}
                  partyType="supplier"
                />
              ) : (
                <Select value={expenseLedgerId} onValueChange={setExpenseLedgerId}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select expense ledger..." />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseLedgers.map((l) => (
                      <SelectItem key={l.id} value={l.id} className="text-xs">
                        {l.name} ({l.group})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1 flex items-center justify-between">
                <span>Payment Date *</span>
                {date !== new Date().toISOString().split("T")[0] && (
                  <span className="text-[10px] text-amber-500 font-semibold">Backdated</span>
                )}
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 text-xs font-mono"
                required
              />
            </div>
          </div>

          {/* Paying Account & Mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Paid From (Cash / Bank Account) *
              </label>
              <Select value={bankLedgerId} onValueChange={setBankLedgerId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select paying account..." />
                </SelectTrigger>
                <SelectContent>
                  {cashBankLedgers.map((l) => (
                    <SelectItem key={l.id} value={l.id} className="text-xs">
                      {l.name} ({l.group}) · Bal: ₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Payment Mode *
              </label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select mode..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Transfer">Bank Transfer (NEFT / RTGS / IMPS)</SelectItem>
                  <SelectItem value="UPI">UPI (GPay / PhonePe / Paytm / BHIM)</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Debit/Credit Card">Debit / Credit Card</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount & Instrument Ref */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Amount Paid (₹) *
              </label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-9 text-xs font-mono font-semibold"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                {paymentMode === "Cheque"
                  ? "Cheque Number"
                  : paymentMode === "UPI"
                  ? "UPI Transaction Ref / UTR"
                  : "UTR / Transaction Reference"}
              </label>
              <Input
                type="text"
                placeholder="e.g. UTR-829103 or CHQ-40291"
                value={instrumentNo}
                onChange={(e) => setInstrumentNo(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          {/* Instrument Date & Bank Name */}
          {(paymentMode === "Cheque" || paymentMode === "Bank Transfer") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Instrument / Cheque Date
                </label>
                <Input
                  type="date"
                  value={instrumentDate}
                  onChange={(e) => setInstrumentDate(e.target.value)}
                  className="h-9 text-xs font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Bank / Branch Name
                </label>
                <Input
                  type="text"
                  placeholder="e.g. HDFC Bank, Fort Branch"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          {/* Outstanding Invoice Allocation Section */}
          {openInvoices.length > 0 && (
            <div className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  Allocate Payment Against Unpaid Purchase Invoices ({openInvoices.length})
                </span>
                <span className="text-[11px] text-muted-foreground">Select invoices to adjust</span>
              </div>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {openInvoices.map((inv) => {
                  const remaining = inv.totalDebit - (inv.paidAmount || 0);
                  const isChecked = selectedInvoices[inv.id] !== undefined;
                  return (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between p-2 rounded bg-card border border-border text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleAllocateInvoice(inv.id, remaining, e.target.checked)}
                          className="rounded text-primary"
                        />
                        <div>
                          <span className="font-medium text-foreground">{inv.invoiceNumber || inv.voucherNumber}</span>
                          <span className="text-muted-foreground ml-2 text-[11px]">({inv.date})</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-semibold text-foreground">
                          ₹{remaining.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                        {inv.paidAmount ? (
                          <span className="text-[10px] text-muted-foreground block">
                            (Paid: ₹{inv.paidAmount.toLocaleString("en-IN")})
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Narration */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
              Narration / Notes
            </label>
            <Input
              type="text"
              placeholder="e.g. Paid in full for supply of materials"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Double-Entry Preview */}
          {debitLedger && payingLedger && numAmount > 0 && (
            <div className="p-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-1">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">
                Double-Entry Posting Preview:
              </span>
              <div className="flex justify-between items-center text-foreground font-mono">
                <span>Dr. {debitLedger.name} (Settlement)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-muted-foreground font-mono pl-4">
                <span>Cr. {payingLedger.name} (Disbursement)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

      </form>
    </FormModal>
  );
}
